'use client';

import * as React from 'react';
import './CalendarFilter.css';
import clsx from 'clsx';
import { formatDate } from '@/utils/dateUtils';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import useI18n from '@/hooks/useI18n';

import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  subWeeks,
  subMonths,
  subDays,
} from 'date-fns';

// Mirrors `ALLOWED_FILTERS` in src/hooks/useDateRangeQueryParams.ts — anything
// outside this set is coerced to 'all-time' downstream, so keep them in sync.
type FilterOption =
  | 'last-week'
  | 'last-month'
  | 'all-time'
  | 'last-6-months'
  | 'last-year'
  | 'custom';

interface CalendarFilterProps {
  onDateRangeChange?: (
    startDate: Date | null,
    endDate: Date | null,
    filter: FilterOption,
  ) => void;
  defaultFilter?: FilterOption;
}

// Custom SVG Icons
const CalendarIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <polyline points="6,9 12,15 18,9" />
  </svg>
);

export function CalendarFilter({
  onDateRangeChange,
  defaultFilter = 'all-time',
}: CalendarFilterProps) {
  const { t } = useI18n();
  const [selectedFilter, setSelectedFilter] =
    React.useState<FilterOption>(defaultFilter);
  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>(
    () => {
      const today = new Date();
      return [today, today];
    },
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState<
    'above' | 'below'
  >('below');
  const [startDate, endDate] = dateRange;
  // Track click order ourselves: first click → pendingStartRef, second click
  // → commit [min, max]. If AntD's activeIndex advances naturally (which it
  // does for click-driven selection), onCalendarChange fires with both slots
  // filled on the second click and we commit immediately (Case A). If
  // activeIndex stays at 0 (edge case with hidden inputs), every click arrives
  // in slot 0 and we use pendingStartRef as the fallback (Case B).
  const pendingStartRef = React.useRef<Date | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rangePickerRef = React.useRef<any>(null);
  // True while the user is mid-selection. Clears the controlled `value` prop
  // so the previous committed range doesn't bleed into the new selection.
  const [isPicking, setIsPicking] = React.useState(false);
  // Reset between dropdown sessions so a partial selection abandoned by
  // closing the dropdown doesn't leak into the next open.
  React.useEffect(() => {
    if (!isOpen) {
      pendingStartRef.current = null;
      setIsPicking(false);
    }
  }, [isOpen]);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const quickFilters = [
    { value: 'all-time' as const, label: String(t('common.allTime')) },
    { value: 'last-week' as const, label: String(t('common.lastWeek')) },
    {
      value: 'last-month' as const,
      label: String(t('common.lastMonth')),
    },
    { value: 'last-6-months' as const, label: String(t('common.last6Months')) },
    { value: 'last-year' as const, label: String(t('common.last1Year')) },
  ];
  // Calculate dropdown position based on available space - prioritize above first
  const calculateDropdownPosition = React.useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();

    // Estimated dropdown height (you can adjust this based on your dropdown's actual height)
    const dropdownHeight = 400;

    // Space available above the trigger
    const spaceAbove = triggerRect.top;

    // Priority: Always try to position above first
    // If there's enough space above, position above
    // Otherwise, position below
    if (spaceAbove >= dropdownHeight) {
      setDropdownPosition('above');
    } else {
      setDropdownPosition('below');
    }
  }, []);

  // Handle click outside to close dropdown
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate position when dropdown opens
  React.useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen, calculateDropdownPosition]);

  // Recalculate position on window resize
  React.useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, calculateDropdownPosition]);

  // Initialize with the requested default filter on mount
  React.useEffect(() => {
    const filter = defaultFilter;
    const range = getDateRange(filter);
    setDateRange([range.start, range.end]);
    setSelectedFilter(filter);
    onDateRangeChange?.(range.start, range.end, filter);
  }, []);

  const getDateRange = (filter: FilterOption) => {
    const now = new Date();
    switch (filter) {
      case 'last-week':
        return {
          start: startOfWeek(subWeeks(now, 1)),
          end: endOfWeek(subWeeks(now, 1)),
        };
      case 'last-month':
        // Rolling 30-day window ending today. Matches the backend's
        // `last-month` semantics — "Last Month" = trailing 30 days, NOT
        // the previous calendar month.
        return {
          start: subDays(now, 30),
          end: now,
        };
      case 'last-6-months':
        return {
          start: startOfMonth(subMonths(now, 6)),
          end: now,
        };
      case 'last-year':
        return {
          start: startOfMonth(subMonths(now, 12)),
          end: now,
        };
      case 'all-time':
      default:
        return {
          start: null,
          end: null,
        };
    }
  };

  const handleQuickFilter = (filter: FilterOption) => {
    const range = getDateRange(filter);
    setDateRange([range.start, range.end]);
    setSelectedFilter(filter);
    onDateRangeChange?.(range.start, range.end, filter);
    setIsOpen(false);
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start && end) {
      setDateRange([start, end]);
      setSelectedFilter('custom');
      onDateRangeChange?.(start, end, 'custom');
      setIsOpen(false);
    }
  };

  const getFilterLabel = () => {
    switch (selectedFilter) {
      case 'last-week':
        return String(t('common.lastWeek'));
      case 'last-month':
        return String(t('common.lastMonth'));
      case 'all-time':
        return String(t('common.allTime'));
      case 'last-6-months':
        return String(t('common.last6Months'));
      case 'last-year':
        return String(t('common.last1Year'));
      case 'custom':
        return startDate && endDate
          ? `${formatDate(startDate)} - ${formatDate(endDate)}`
          : startDate
            ? `${formatDate(startDate)} - ${String(t('common.selectEndDate'))}`
            : String(t('common.customRange'));
      default:
        return String(t('common.allTime'));
    }
  };

  return (
    <div className="calendar-filter-container" ref={dropdownRef}>
      <button
        ref={triggerRef}
        className="calendar-filter-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CalendarIcon />
        {getFilterLabel()}
        <ChevronDownIcon />
      </button>
      {isOpen && (
        <div
          className={clsx(
            dropdownPosition === 'above'
              ? 'calendar-filter-dropdown-above'
              : 'calendar-filter-dropdown-below',
          )}
        >
          <div className="calendar-filter-content">
            {/* Quick Filters */}
            <div className="calendar-filter-tabs">
              {quickFilters.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => handleQuickFilter(filter.value)}
                  className={clsx(
                    'calendar-filter-tab',
                    selectedFilter === filter.value &&
                      'calendar-filter-tab-active',
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <hr className="calendar-filter-divider" />
            {/* Date Picker */}
            <div className="calendar-filter-picker">
              <DatePicker.RangePicker
                ref={rangePickerRef}
                // Always pass a controlled value so AntD never switches between
                // uncontrolled and controlled (that transition is a no-op for
                // AntD's visual layer and prevents the blue circle from landing).
                // • isPicking  → [pendingStart, null]: marks the first-click date
                //   as range-start (blue circle) and puts AntD in "waiting for
                //   end" mode so the hover-range preview works.
                // • committed  → [start, end]: shows the saved range on reopen.
                // • empty      → [null, null]: fully controlled, nothing selected.
                value={
                  isPicking && pendingStartRef.current
                    ? [dayjs(pendingStartRef.current), null]
                    : startDate && endDate
                      ? [dayjs(startDate), dayjs(endDate)]
                      : [null, null]
                }
                disabledDate={(current) =>
                  !!current && current.toDate() > new Date()
                }
                onCalendarChange={(values) => {
                  // Case A: AntD advanced activeIndex normally → both slots
                  // filled on the second click. Only trust this after the
                  // first click has cleared the old range (isPicking = true),
                  // otherwise the stale committed end date would leak in.
                  if (isPicking && values?.[0] && values?.[1]) {
                    pendingStartRef.current = null;
                    setIsPicking(false);
                    const s = values[0].toDate();
                    const e = values[1].toDate();
                    handleDateChange([
                      s.getTime() <= e.getTime() ? s : e,
                      s.getTime() <= e.getTime() ? e : s,
                    ]);
                    return;
                  }

                  // Case B: activeIndex stayed at 0, or this is the first
                  // click — track order manually.
                  const clickedDayjs = values?.[0] ?? values?.[1] ?? null;
                  const clicked = clickedDayjs?.toDate() ?? null;
                  if (!clicked) return;

                  if (pendingStartRef.current === null) {
                    // First click: clear old range, enter picking mode.
                    pendingStartRef.current = clicked;
                    setIsPicking(true);
                    // Advance AntD's activeIndex from 0 → 1 by focusing the
                    // selector's end input via the picker ref. The popup has
                    // no inputs of its own — only the selector (trigger) does,
                    // and onSelectorFocus(event, index) is the only path that
                    // calls setActiveIndex. With activeIndex=1, AntD computes
                    // hoverRangeValue as [pendingStart, cursor], so the start
                    // date keeps its blue circle while the user picks the end.
                    setTimeout(() => {
                      rangePickerRef.current?.focus({ index: 1 });
                    }, 0);
                    return;
                  }

                  const first = pendingStartRef.current;
                  pendingStartRef.current = null;
                  setIsPicking(false);
                  const [start, end] =
                    first.getTime() <= clicked.getTime()
                      ? [first, clicked]
                      : [clicked, first];
                  handleDateChange([start, end]);
                }}
                allowClear={false}
                open={true}
                getPopupContainer={() =>
                  document.querySelector(
                    '.calendar-filter-content',
                  ) as HTMLElement
                }
                popupClassName="inline-calendar-popup"
                inputReadOnly
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
